<template>
  <div>
    <!-- 外部运营费用配置主弹窗：展示配置列表并提供新增、编辑、删除入口 -->
    <el-dialog
      title="外部运营费用配置"
      :visible.sync="dialogVisible"
      width="1000px"
      top="8vh"
      :close-on-click-modal="false"
      custom-class="import-dialog"
      @close="handleClose"
    >
      <div class="dialog-toolbar">
        <el-button type="primary" @click="handleAdd">新增</el-button>
      </div>
      <el-table
        v-loading="listLoading"
        :data="tableList"
        border
        empty-text="暂无数据"
        style="width: 100%;"
        height="450"
      >
        <el-table-column prop="agentNames" label="团长名称" min-width="200">
          <template slot-scope="{ row }">
            <el-tooltip :content="formatAgentNames(row)" placement="top" effect="dark">
              <div class="agent-name-cell">{{ formatAgentNames(row) }}</div>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="平台扣点计算规则" min-width="180">
          <template slot-scope="{ row }">
            实际收入 × {{ formatRate(row.platformDeductRate) }}%
          </template>
        </el-table-column>
        <el-table-column label="平台返点计算规则" min-width="180">
          <template slot-scope="{ row }">
            实际收入 × {{formatRate(row.platformReturnRate) }}%
          </template>
        </el-table-column>
        <el-table-column label="守兔扣点计算规则" min-width="180">
          <template slot-scope="{ row }">
            实际收入 × {{ formatRate(row.shoutuDeductRate) }}%
          </template>
        </el-table-column>
        <el-table-column label="操作" width="130" align="center">
          <template slot-scope="{ row }">
            <div class="operation-links">
              <div @click="handleEdit(row)" class="succes-button">编辑</div>
              <div @click="handleDelete(row)" class="delete-button">删除</div>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        :current-page="pagination.pageNo"
        :page-size="pagination.pageSize"
        :page-sizes="[10, 20, 30, 40]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
      <template slot="footer">
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="listLoading" @click="dialogVisible = false">确认</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 新增、编辑共用子弹窗，保存成功后通知主弹窗刷新列表 -->
    <external-fee-config-form ref="formRef" @success="handleFormSuccess" />
  </div>
</template>

<script>
import poi from '@/api/poi'
import ExternalFeeConfigForm from './externalFeeConfigForm.vue'

const PoiModel = new poi()

// 从网络异常中提取可读文案；没有明确错误时使用业务兜底提示。
function getErrorMessage(error, fallback) {
  return error && error.response && error.response.data && error.response.data.message
    ? error.response.data.message
    : error && error.message
      ? error.message
      : fallback
}

// 兼容项目接口同时使用 code=200 和 success=true 的成功语义。
function isSuccess(response) {
  const body = response && response.data
  return !!body && (body.code === 200 || body.success === true)
}

export default {
  name: 'ExternalFeeConfigPop',
  components: { ExternalFeeConfigForm },
  data() {
    return {
      dialogVisible: false,
      listLoading: false,
      deletingId: null,
      requestId: 0,
      tableList: [],
      total: 0,
      totalPage: 0,
      pagination: {
        pageNo: 1,
        pageSize: 10
      }
    }
  },
  methods: {
    // 每次打开主弹窗都重置分页，避免沿用上一次浏览位置。
    open() {
      this.pagination.pageNo = 1
      this.pagination.pageSize = 10
      this.dialogVisible = true
      this.getList()
    },
    // 新增不传行数据，子弹窗据此生成不含 id 的保存参数。
    handleAdd() {
      this.$refs.formRef.open()
    },
    // 编辑必须携带配置 id，具体详情与回显由子弹窗加载。
    handleEdit(row) {
      if (!row || row.id === undefined || row.id === null || row.id === '') {
        this.$message.error('配置ID不能为空')
        return
      }
      this.$refs.formRef.open(row)
    },
    // 删除成功后刷新当前分页；删除本页最后一条时自动回退一页。
    async handleDelete(row) {
      if (!row || row.id === undefined || row.id === null || row.id === '') {
        this.$message.error('配置ID不能为空')
        return
      }
      if (this.deletingId !== null) return
      let deleteSucceeded = false
      try {
        await this.$confirm('确认删除这条规则？', '提示', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          // MessageBox 实例会复用，打开时显式清除上一次删除残留的 loading 状态。
          confirmButtonLoading: false,
          // 删除请求完成前保持确认框打开，并让确认按钮显示 loading。
          beforeClose: (action, instance, done) => {
            if (action !== 'confirm') {
              done()
              return
            }
            if (instance.confirmButtonLoading) return
            this.deletingId = row.id
            instance.confirmButtonLoading = true
            instance.confirmButtonText = '删除中...'
            PoiModel.deleteExternalFeeConfig({ id: row.id })
              .then(res => {
                if (!isSuccess(res)) {
                  this.$message.error((res && res.data && (res.data.message || res.data.info)) || '删除失败')
                  return
                }
                deleteSucceeded = true
                this.$message.success('删除成功')
                // done() 会触发 MessageBox 复用，先恢复按钮状态避免下次打开继续 loading。
                instance.confirmButtonLoading = false
                instance.confirmButtonText = '删除'
                done()
              })
              .catch(error => {
                this.$message.error(getErrorMessage(error, '删除失败，请稍后重试'))
              })
              .finally(() => {
                if (!deleteSucceeded) {
                  this.deletingId = null
                  instance.confirmButtonLoading = false
                  instance.confirmButtonText = '删除'
                }
              })
          }
        })
      } catch (error) {
        return
      }
      if (!deleteSucceeded) return
      try {
        if (this.tableList.length === 1 && this.pagination.pageNo > 1) {
          this.pagination.pageNo -= 1
        }
        await this.getList()
      } finally {
        this.deletingId = null
      }
    },
    handleFormSuccess() {
      this.getList()
    },
    // 关闭时使未完成的列表请求失效，避免响应返回后继续更新已关闭弹窗。
    handleClose() {
      this.requestId += 1
      this.listLoading = false
      this.deletingId = null
    },
    // requestId 只允许最后一次请求写入列表，避免快速翻页产生数据倒灌。
    async getList() {
      const requestId = ++this.requestId
      this.listLoading = true
      try {
        const res = await PoiModel.getExternalFeeConfigList(this.pagination)
        if (requestId !== this.requestId) return
        if (!isSuccess(res)) {
          this.$message.error((res && res.data && (res.data.message || res.data.info)) || '获取外部运营费用配置失败')
          return
        }
        const data = res.data.data || {}
        this.tableList = Array.isArray(data) ? data : (data.list || [])
        this.total = Number(data.total || this.tableList.length || 0)
        this.totalPage = Number(data.totalPage || (this.total ? Math.ceil(this.total / this.pagination.pageSize) : 0))
        if (data.pageNo) this.pagination.pageNo = data.pageNo
        if (data.pageSize) this.pagination.pageSize = data.pageSize
      } catch (error) {
        if (requestId !== this.requestId) return
        this.$message.error(getErrorMessage(error, '获取外部运营费用配置失败'))
      } finally {
        if (requestId === this.requestId) this.listLoading = false
      }
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.pageNo = 1
      this.getList()
    },
    handleCurrentChange(page) {
      this.pagination.pageNo = page
      this.getList()
    },
    // 团长名称接口可能返回字符串或数组，统一格式化后展示。
    formatAgentNames(row) {
      if (Array.isArray(row.agentNames)) return row.agentNames.join('、')
      return row.agentNames || (Array.isArray(row.userUUIDs) ? row.userUUIDs.join('、') : '-')
    },
    formatRate(rate) {
      return rate === undefined || rate === null || rate === '' ? '-' : rate
    }
  }
}
</script>

<style scoped lang="less">
/deep/.el-dialog.import-dialog {
  max-height: 830px !important;
  // max-height: 800px;
  overflow-y: auto;
  // margin: auto !important;
}
.dialog-toolbar {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
.agent-name-cell {
  display: -webkit-box;
  overflow: hidden;
  max-height: 40px;
  line-height: 20px;
  word-break: break-all;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.operation-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}
.operation-links .el-button {
  margin-left: 0;
  padding: 0 4px;
}
.operation-links .delete-button {
  color: #f56c6c;
  cursor: pointer;
}
.operation-links .delete-button:hover {
  color: #f78989;
}
.succes-button{
  color: #2d8cf0;
  cursor: pointer;
  margin-right: 4px;
}
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 15px;
}
</style>



// WEBPACK FOOTER //
// src/views/pc/miniProgram/poiCps/components/externalFeeConfigPop.vue